import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Typography,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { toast as toastFn } from "react-toastify";
import { generateCourseCode, imageFileFilter } from "../../utils";
import { course as courseConst } from "./constants";
import { GET_COURSE_ALL, uploadImage } from "../../store/course/courseBaseApi";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SearchableSelect from "../../components/SearchableSelect";
import Button from "../../components/Button";
import TextField from "../../components/TextField";
import { useTheme } from "@mui/material/styles";
import { useCourse } from "./hooks";
import { Info } from "@phosphor-icons/react";
import { MediaPreview } from "../questionBank/MediaPreview";

export const Edit = () => {
  const theme = useTheme();

  const { id } = useParams();
  const navigate = useNavigate();
  const {
    current,
    loadCurrent,
    resetCurrent,
    loading,
    save,
    savePrerequisites,
    create,
  } = useCourse();
  const [course, setCourse] = useState(current);
  const [duration, setDuration] = useState({});
  const mediaTypes = ["link", "upload"];
  const [selectedTab, setSelectedTab] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(false);
  const [currentContentUrl, setCurrentContentUrl] = useState(null);
  const [currentContentType, setCurrentContentType] = useState(null);

  const [input, setInput] = useState("");
  const [prerequisites, setPrerequisites] = useState(current.prerequisites);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    setTimeout(() => {
      if (id) {
        resetCurrent();
        loadCurrent({ id });
      } else {
        setCourse({
          courseName: "",
          // code: generateCourseCode(),
          application: courseConst.APPLICATIONS.Intelliread,
          type: courseConst.TYPES.Standard,
          category: courseConst.CATEGORY.Course,
          location: courseConst.LOCATIONS.Online,
          courseMode: "TIMED",
          description: "Course",
          studentType: "URBAN",
          // Add other fields as necessary
        });
        setDuration({
          noOfSessions: "",
          sessionTime: "",
        });
        setPrerequisites([]);
      }
    }, 5);
  }, [id]);

  useEffect(() => {
    if (!Object.keys(current).length) return;
    let { duration } = current;

    setCourse({
      ...current,
      application: current.application || courseConst.APPLICATIONS.Intelliread,
    });
    setDuration(duration && duration.length ? duration[0] : {});
    setPrerequisites(
      (current.prerequisites || []).map((e) => ({
        courseName: e.course,
        id: e.courseId,
        ...e,
      })),
    );
  }, [current]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCourse((prevCourse) => ({
      ...prevCourse,
      [name]: value,
    }));
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleMediaPreview = (url, type) => {
    setCurrentContentUrl(url);
    setCurrentContentType(type);
    setMediaPreview(!mediaPreview);
  };

  const handlePreviewClose = () => {
    setCurrentContentUrl(null);
    setCurrentContentType(null);
    setMediaPreview(!mediaPreview);
  };

  const onFileChange = async (file, field) => {
    const fileSizeInMB = file.size / 1024 / 1024;
    if (!imageFileFilter(file.name)) {
      return showToast("Please upload a valid image file", "error");
    } else if (fileSizeInMB > 5) {
      return showToast("Please upload file with in the 5 MB", "error");
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      const resp = await uploadImage({ id, formData, field });
      if (resp.data) {
        setCourse((prevCourse) => ({
          ...prevCourse,
          [field]: resp.data,
        }));
      }

      if (resp.message) {
        showToast(resp.message, "success");
      }
    } catch (e) {
      return showToast(e.error, "error");
    } finally {
      setUploading(false);
    }
  };

  const prerequisitesOnChange = (e, value) => {
    let prerequisites = value.map((item) => ({
      course: item.courseName,
      courseId: item.id,
      type: courseConst.PREREQUISITE_TYPES.Mandatory,
    }));

    if (id) {
      savePrerequisites({ id, body: { prerequisites } });
    }

    setPrerequisites(
      (prerequisites || []).map((e) => ({
        courseName: e.course,
        id: e.courseId,
        ...e,
      })),
    );
  };

  const showToast = (msg, type) => {
    if (!type) {
      return toastFn(msg);
    }

    toastFn[type](msg);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      ...course,
      id,
      duration: [duration],
      prerequisites,
    };

    if (id) {
      save({ course: data, navigate });
    } else {
      create({ course: data, navigate });
    }
    setCourse({});
    setDuration({});
    setPrerequisites([]);
  };

  return (
    <Box
      sx={{
        padding: "0px 20px",
        [theme.breakpoints.down("md")]: {
          padding: "0px",
        },
      }}
    >
      <div className="course header-container">
        <div className="header">
          <Typography
            align="left"
            variant="h1"
            color="primary.blue"
            sx={{
              margin: "0",
              fontWeight: "700",
              lineHeight: "1.5",
              fontSize: "2rem",
            }}
          >
            {id ? `${course.courseName}` : "Create Course"}
          </Typography>
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        <Grid
          sx={styles.gridContainer}
          container
          spacing={2}
          alignItems="center"
        >
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>
              Name <span style={{ color: "red" }}>*</span>{" "}
            </InputLabel>
            <TextField
              name="courseName"
              fullWidth
              value={course.courseName}
              onChange={handleChange}
              placeholder="Enter Course name"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>
              No of Chapters <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <TextField
              name="noOfSessions"
              fullWidth
              type="number"
              value={duration.noOfSessions}
              onChange={({ target }) => {
                if (target.value >= 0) {
                  setDuration((prev) => ({
                    ...prev,
                    [target.name]: target.value,
                  }));
                }
              }}
              onWheel={(e) => e.preventDefault()}
              placeholder="No Of Chapters"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>
              Chapter Time <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <TextField
              name="sessionTime"
              fullWidth
              type="number"
              value={duration.sessionTime}
              onChange={({ target }) => {
                if (target.value >= 0) {
                  setDuration((prev) => ({
                    ...prev,
                    [target.name]: target.value,
                  }));
                }
              }}
              inputProps={{
                onWheel: (e) => e.preventDefault(), // Disable mouse wheel interaction
              }}
              placeholder="Session Time"
            />
          </Grid>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography
              align="left"
              variant="h6"
              color="primary.blue"
              sx={{
                margin: "0",
                fontWeight: "700",
                lineHeight: "1.5",
              }}
            >
              Requirements
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>
              Category <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <FormControl fullWidth>
              <Select
                name="category"
                value={String(course.category)}
                onChange={handleChange}
                sx={{ height: "47px" }}
                disabled
              >
                {Object.keys(courseConst.CATEGORY).map((key, i) => (
                  <MenuItem key={i} value={key}>
                    {courseConst.CATEGORY[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>
              Location <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <FormControl fullWidth>
              <Select
                name="location"
                value={String(course.location)}
                onChange={handleChange}
                sx={{ height: "47px" }}
              >
                {Object.keys(courseConst.LOCATIONS).map((key, i) => (
                  <MenuItem key={i} value={key}>
                    {courseConst.LOCATIONS[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>
              Mode <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <FormControl fullWidth>
              <Select
                name="courseMode"
                value={String(course.courseMode)}
                onChange={handleChange}
                sx={{ height: "47px" }}
              >
                {Object.keys(courseConst.MODES).map((key, i) => (
                  <MenuItem key={i} value={key}>
                    {courseConst.MODES[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputLabel style={styles.inputLabel}>
              Student Type <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <FormControl fullWidth>
              <Select
                name="studentType"
                value={String(course.studentType || "URBAN")}
                onChange={handleChange}
                sx={{ height: "47px" }}
              >
                {Object.keys(courseConst.STUDENT_TYPE).map((key, i) => (
                  <MenuItem key={i} value={key}>
                    {courseConst.STUDENT_TYPE[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} sx={{ mt: 1 }}>
            <InputLabel style={styles.inputLabel}>Prerequisites</InputLabel>
            <SearchableSelect
              endpoint={GET_COURSE_ALL}
              onChange={prerequisitesOnChange}
              queryFieldName="search"
              multiple={true}
              defaultValue={prerequisites}
              getOptionLabel={(opt) => opt.courseName}
              sx={{
                height: "47px",
                "& .MuiInputBase-root": {
                  height: "47px",
                },
              }}
            />
          </Grid>
          {/* image field is not needed  */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography
              align="left"
              variant="h6"
              color="primary.blue"
              sx={{
                margin: "0",
                fontWeight: "700",
                lineHeight: "1.5",
              }}
            >
              Image
              <Tooltip title="Provide link or Upload file do not exceeds 5 MB">
                <Info style={{ fontSize: "16px", cursor: "pointer" }} />
              </Tooltip>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
            >
              {mediaTypes.map((item, index) => (
                <Tab label={item} key={index} />
              ))}
            </Tabs>
          </Grid>
          {selectedTab === 0 && (
            <Grid item xs={6}>
              <InputLabel style={styles.inputLabel}>URL</InputLabel>
              <Grid container alignItems="center">
                <Grid item xs={11}>
                  <TextField
                    name="imageUrl"
                    fullWidth
                    value={course.imageUrl || ""}
                    placeholder={"Enter Media Link"}
                    onChange={(e) => handleChange(e)}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          {selectedTab === 1 && (
            <Grid item xs={6}>
              <InputLabel style={styles.inputLabel}>Image</InputLabel>
              <Grid container alignItems="center">
                <Grid item xs={11}>
                  <TextField
                    name="imageUrl"
                    fullWidth
                    value={course.imageUrl || ""}
                    placeholder={"Select image"}
                    disabled
                  />
                </Grid>
                <Grid item xs={1}>
                  {uploading && <CircularProgress size="30px" />}
                  {!uploading && (
                    <FileUploadBtn
                      onFileSelect={(file) => onFileChange(file, "imageUrl")}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
          {course?.imageUrl && (
            <Grid item xs={12}>
              <Button
                onClick={() =>
                  handleMediaPreview(course.imageUrl, "text-and-image")
                }
                variant="contained"
                size="small"
                color="primary"
                placeholder={" View Image"}
              />

              <MediaPreview
                contentURL={currentContentUrl}
                contentType={currentContentType}
                handleClose={handlePreviewClose}
                mediaPreview={mediaPreview}
              />
            </Grid>
          )}
        </Grid>
        <Box sx={{ mt: 4 }}>
          <Button
            size="large"
            type="submit"
            variant="contained"
            color="primary"
            placeholder={!id ? "Create" : "Save Changes"}
          />
        </Box>
      </form>
    </Box>
  );
};

const FileUploadBtn = ({ onFileSelect }) => {
  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onFileSelect(file);
    }
  };

  return (
    <div>
      <Button
        style={styles.uploadBtn}
        variant="contained"
        placeholder={
          <FileUploadIcon sx={{ color: "var(--mui-palette-primary-blue)" }} />
        }
        onClick={handleButtonClick}
      />
      <input
        type="file"
        style={styles.hiddenInput}
        ref={fileInputRef}
        onChange={handleFileSelect}
      />
    </div>
  );
};

const styles = {
  label: {
    color: "var(--mui-palette-common-black)",
    fontWeight: "bold",
  },
  inputLabel: {
    color: "var(--mui-palette-primary-darkGrey)",
    marginBottom: "5px",
    fontWeight: "600",
    fontSize: "14px",
  },
  gridContainer: {
    mt: 2,
  },
  suggestionBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "10px",
  },
  suggestionItem: {
    padding: "10px",
    border: "1px solid #ccc",
    borderRadius: "2px",
    cursor: "pointer",
    boxShadow: "0 4px 8px var(--mui-palette-secondary-boxShadow1)",
    backgroundColor: "var(--mui-palette-gray-lightGrey300)",
  },
  uploadBtn: {
    width: "30px",
    height: "30px",
    boxShadow: "none",
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    borderRadius: "0",
    minWidth: "30px",
  },
  hiddenInput: {
    display: "none",
  },
};

export default Edit;
